import React, { useState, useEffect } from 'react';
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import { Shield, ArrowLeft } from 'lucide-react';
import { trackSubscription, trackError } from '../Utils/analytics';
import './StripePaymentForm.css';

interface StripePaymentFormProps {
  selectedPlan: string;
  stripePromise: Promise<Stripe | null>;
  onSuccess: () => void;
  onBack: () => void; // This prop is for the outer component to handle navigation
}

interface CheckoutFormProps {
  selectedPlan: string;
  clientSecret: string;
  onSuccess: () => void;
  onBack: () => void;
  finalPlanDetails: { name: string; amount: number; interval: string; currency: string; display: string; };
}

// Inner component that uses Stripe hooks
const CheckoutForm: React.FC<CheckoutFormProps> = ({
  selectedPlan, 
  clientSecret,
  onSuccess, 
  onBack,
  finalPlanDetails
}) => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [isLoading, setIsLoading] = useState(false); // State for form submission loading
  const [error, setError] = useState<string>(''); // State for form submission errors

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Payment system not ready. Please try again.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Determine return URL based on environment
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const currentDomain = isDevelopment ? 'http://localhost:3000' : 'https://homecookt.com';
      
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${currentDomain}/payment-success`,
        },
      });

      if (stripeError) {
        // Handle Stripe errors
        if (stripeError.type === 'card_error') {
          setError(`Card Error: ${stripeError.message}`);
          trackError('stripe_card_error', 'stripe_payment_form');
        } else if (stripeError.type === 'validation_error') {
          setError(`Validation Error: ${stripeError.message}`);
          trackError('stripe_validation_error', 'stripe_payment_form');
        } else {
          setError(stripeError.message || 'Payment failed. Please try again.');
          trackError('stripe_payment_error', 'stripe_payment_form');
        }
      } else {
        // Payment succeeded - this will redirect to return_url
        trackSubscription('payment_completed', selectedPlan);
        onSuccess();
      }
    } catch (error) {
      console.error('Payment confirmation error:', error);
      setError('Payment processing failed. Please try again.');
      trackError('stripe_confirm_error', 'stripe_payment_form');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="stripe-payment-form">
      <div className="stripe-form-header">
        <h3 className="stripe-form-title">Complete Payment</h3>
        <p className="stripe-form-subtitle"> {/* Use finalPlanDetails for display */}
          {finalPlanDetails.name} - {finalPlanDetails.display}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="stripe-payment-element">
          <PaymentElement 
            options={{
              layout: 'tabs',
             paymentMethodOrder: ['card', 'google_pay', 'apple_pay', 'upi', 'amazon_pay', 'revolut_pay']
            }}
          />
        </div>

        {error && (
          <div className="stripe-error-message">{error}</div>
        )}

        <button 
          type="submit" 
          disabled={!stripe || !elements || isLoading}
          className="stripe-submit-button"
        >
          {isLoading ? ( // Show loading spinner when submitting
            <>
              <div className="stripe-loading-spinner"></div>
              Processing...
            </>
          ) : (
            `Pay ${finalPlanDetails.display}`
          )}
        </button> {/* Use finalPlanDetails.display for button text */}

        <button 
          type="button" 
          onClick={onBack} 
          className="stripe-back-button"
          disabled={isLoading}
        >
          <ArrowLeft size={16} />
          Back to Payment Options
        </button>
      </form>

      <div className="stripe-security-notice">
        <Shield size={16} />
        <span>Secured by Stripe - Your payment information is encrypted</span>
      </div>
    </div>
  );
};

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  selectedPlan,
  stripePromise,
  onSuccess,
  onBack,
}) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null); // Stripe client secret for PaymentElement
  const [isInitializing, setIsInitializing] = useState(true); // Loading state for initial API call
  const [error, setError] = useState<string>(''); // Error state for initial API call
  const [finalPlanDetails, setFinalPlanDetails] = useState<{
    name: string;
    amount: number;
    interval: string;
    currency: string;
    display: string;
  } | null>(null); // Stores the final plan details including currency from backend

  // Base plan pricing (amount in minor units, e.g., 499 for £4.99)
  const basePlanPricing = React.useMemo(() => {
    return {
      weekly: { name: 'Weekly Plan', amount: 399, interval: 'week' },
      monthly: { name: 'Monthly Plan', amount: 499, interval: 'month' },
      yearly: { name: 'Yearly Plan', amount: 4999, interval: 'year' },
    };
  }, []);

  // Get the selected base plan details
  const currentBasePlan =
    basePlanPricing[selectedPlan as keyof typeof basePlanPricing] ||
    basePlanPricing.monthly;

  // Initialize payment intent when component mounts
  useEffect(() => {
    const initializePayment = async () => {
      setIsInitializing(true);
      setError('');

      try {
        const authToken = localStorage.getItem('authToken');
      const userCountryCode = localStorage.getItem('userCountryCode') || 'GB'; // Default to GB if not found

        if (!authToken) {
          setError('Authentication required. Please log in to continue.');
          setIsInitializing(false);
          return;
        }

      console.log('Creating Stripe payment intent for plan:', currentBasePlan);

        console.log( currentBasePlan.name,
          currentBasePlan.amount, // Send base amount
           currentBasePlan.interval,
        )

      const response = await fetch('https://api.homecookt.com/api/subscribe/stripe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        
        body: JSON.stringify({
          plan_name: currentBasePlan.name,
          plan_amount: currentBasePlan.amount, // Send base amount
          plan_interval: currentBasePlan.interval,
          payment_method: 'cards', // Still indicate payment method type
        }),
      });

      const data = await response.json();
      if (response.ok) {
        if (data.stripe_client_secret) {
          setClientSecret(data.stripe_client_secret);

          // Use actual currency and amount from backend response
          const actualCurrency = data.currency || 'gbp'; // Fallback to gbp if not provided
          const actualAmount = data.amount || currentBasePlan.amount; // Fallback to base amount if not provided
          const displayAmount = (actualAmount / 100).toFixed(2);
          
          // Format currency symbol based on currency code
          const getCurrencySymbol = (currency: string) => {
            switch (currency.toLowerCase()) {
              case 'inr': return '₹';
              case 'usd': return '$';
              case 'gbp': return '£';
              case 'eur': return '€';
              default: return currency.toUpperCase() + ' ';
            }
          };
          
          const currencySymbol = getCurrencySymbol(actualCurrency);
          const displayString = `${actualCurrency.toUpperCase()} ${currencySymbol}${displayAmount}/${currentBasePlan.interval}`;

          setFinalPlanDetails({
            name: currentBasePlan.name,
            amount: actualAmount,
            interval: currentBasePlan.interval,
            currency: actualCurrency,
            display: displayString,
          });

          // Store payment details for success/cancel pages
          localStorage.setItem('currentStripePaymentType', data.type);
          localStorage.setItem('currentStripePlan', selectedPlan);
          localStorage.setItem('currentStripeAmount', displayString);

          if (data.type === 'subscription') {
            localStorage.setItem('currentStripeSubscriptionId', data.subscription_id);
          } else if (data.type === 'payment_intent') {
            localStorage.setItem('currentStripePaymentIntentId', data.payment_intent_id);
          }
        }
        trackSubscription('payment_started', selectedPlan);
      } else {
        setError(data.error || 'Failed to initialize payment. Please try again.');
        trackError('stripe_api_error', 'stripe_payment_form');
      }
      } catch (error) {
        console.error('Stripe initialization error:', error);
        setError('Network error. Please check your connection and try again.');
        trackError('stripe_network_error', 'stripe_payment_form');
      } finally {
        setIsInitializing(false);
      }
    };

    initializePayment();
  }, [selectedPlan, currentBasePlan]);

  if (isInitializing) {
    return (
      <div className="stripe-payment-form">
        <div className="stripe-form-header"> {/* Display currentBasePlan while initializing */}
          <h3 className="stripe-form-title">Initializing Payment</h3>
          <p className="stripe-form-subtitle">Setting up secure payment for {currentBasePlan.name}</p>
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: '3rem',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div className="stripe-loading-spinner"></div>
          <p style={{ color: '#666', textAlign: 'center' }}>Preparing your payment...</p>
        </div>
      </div>
    );
  }

  if (error && !clientSecret) {
    return (
      <div className="stripe-payment-form">
        <div className="stripe-form-header">
          <h3 className="stripe-form-title">Payment Error</h3>
        </div>
        <div className="stripe-error-message">{error}</div>
        <button onClick={onBack} className="stripe-back-button">
          <ArrowLeft size={16} />
          Back to Payment Options
        </button>
      </div>
    );
  }

  if (!clientSecret || !finalPlanDetails) {
    return (
      <div className="stripe-payment-form">
        <div className="stripe-form-header">
          <h3 className="stripe-form-title">Loading Payment</h3>
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: '3rem'
        }}>
          <div className="stripe-loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Only render Elements provider when we have clientSecret
  return ( // Pass finalPlanDetails to CheckoutForm
    <Elements 
      stripe={stripePromise} 
      options={{ 
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#635bff',
            colorBackground: '#ffffff',
            colorText: '#1f2937',
            colorDanger: '#dc3545',
            fontFamily: 'Inter, system-ui, sans-serif',
            spacingUnit: '4px',
            borderRadius: '8px',
          }
        }
      }}
    >
      <CheckoutForm
        selectedPlan={selectedPlan}
        clientSecret={clientSecret}
        onSuccess={onSuccess}
        onBack={onBack}
        finalPlanDetails={finalPlanDetails}
      />
    </Elements>
  );
};

export default StripePaymentForm;